import { useEffect } from "react";
import jesusIdle from "@/assets/jesus_idle.jpg";
import jesusAnswered from "@/assets/jesus_answered.jpg";

/**
 * JesusPortrait — cross-fading illustration of Jesus.
 *
 * Two states:
 *   "idle"       → only `jesus_idle.jpg` visible (phone resting on bench)
 *   "answered"   → only `jesus_answered.jpg` visible (phone to ear)
 *
 * Cross-fade durations are caller-driven (default 900ms idle→answered, 600ms
 * answered→idle). Layout never shifts: both images are absolutely positioned
 * inside a square wrapper with a fixed aspect ratio. Both assets are also
 * pre-loaded once at module load so the swap is instant on first run.
 *
 * Optional: `breathing` adds a very subtle scale pulse on the answered image
 * (used during `speaking` state on the call screen).
 * Optional: `glow` brightens a halo behind the answered image (during call).
 */

// Pre-load on first import so the answered frame is in the cache before
// the user clicks Call.
if (typeof window !== "undefined") {
  [jesusIdle, jesusAnswered].forEach((src) => {
    const img = new Image();
    img.src = src;
  });
}

export default function JesusPortrait({
  variant = "idle",          // "idle" | "answered"
  breathing = false,
  glow = false,
  fadeInMs = 900,
  fadeOutMs = 600,
  size = "md",               // "sm" | "md" | "lg"  → max-width tiers
  className = "",
  testId = "jesus-portrait",
}) {
  // Track whether component is mounted so we can skip the very first
  // transition (no flash on initial render).
  useEffect(() => { /* no-op; just ensures effect order */ }, []);

  const sizeMaxW = {
    sm: "max-w-[240px] sm:max-w-[280px]",
    md: "max-w-[300px] sm:max-w-[360px] md:max-w-[420px]",
    lg: "max-w-[340px] sm:max-w-[420px] md:max-w-[520px]",
  }[size] || "";

  const showAnswered = variant === "answered";

  return (
    <div
      data-testid={testId}
      data-variant={variant}
      className={`relative w-full ${sizeMaxW} aspect-square mx-auto select-none pointer-events-none ${className}`}
    >
      {/* Warm glow halo behind the answered image */}
      <div
        aria-hidden
        className="absolute inset-[-12%] rounded-full blur-3xl transition-opacity ease-out"
        style={{
          background:
            "radial-gradient(closest-side, hsl(36 70% 65% / 0.55), hsl(36 60% 60% / 0.18) 60%, transparent 75%)",
          opacity: showAnswered && glow ? 1 : 0,
          transitionDuration: `${showAnswered ? fadeInMs : fadeOutMs}ms`,
        }}
      />

      {/* Idle frame */}
      <img
        src={jesusIdle}
        alt=""
        loading="eager"
        decoding="async"
        draggable={false}
        className="absolute inset-0 h-full w-full object-contain transition-opacity ease-out"
        style={{
          opacity: showAnswered ? 0 : 1,
          transitionDuration: `${showAnswered ? fadeInMs : fadeOutMs}ms`,
          filter: "drop-shadow(0 24px 36px rgba(26, 32, 48, 0.18))",
        }}
      />

      {/* Answered frame */}
      <img
        src={jesusAnswered}
        alt=""
        loading="eager"
        decoding="async"
        draggable={false}
        className={`absolute inset-0 h-full w-full object-contain transition-opacity ease-out ${
          breathing && showAnswered ? "animate-breathe-soft" : ""
        }`}
        style={{
          opacity: showAnswered ? 1 : 0,
          transitionDuration: `${showAnswered ? fadeInMs : fadeOutMs}ms`,
          filter: "drop-shadow(0 24px 40px rgba(26, 32, 48, 0.22))",
          transformOrigin: "center 60%",
        }}
      />
    </div>
  );
}
